import React, { useEffect, useState, useRef } from "react";
import {
  collection,
  addDoc,
  onSnapshot,
  serverTimestamp,
  query,
  orderBy,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../services/firebaseConfig";

import "./chatbox.css";

const ChatBox = ({ hashcode, userType = "organiser" }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeout = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!hashcode) return;

    const q = query(
      collection(db, `sharedChats/${hashcode}/messages`),
      orderBy("timestamp", "asc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setMessages(msgs);
    });

    return () => unsubscribe();
  }, [hashcode]);

  useEffect(() => {
  const chatBody = document.querySelector(".chatbox-body");
  if (chatBody && bottomRef.current) {
    chatBody.scrollTop = chatBody.scrollHeight;
  }
}, [messages]);

  const containsProhibitedInfo = (text) => {
    const emailRegex = /[\w.-]+@[\w.-]+\.\w+/gi;
    const phoneRegex =
      /(\+?\d{1,4}[\s-]?)?(\(?\d{2,4}\)?[\s-]?)?[\d\s-]{6,}/g;
    return emailRegex.test(text) || phoneRegex.test(text);
  };

  const sendMessage = async () => {
    const trimmedText = input.trim();
    if (!trimmedText && !pendingFile) return;

    if (trimmedText && containsProhibitedInfo(trimmedText)) {
      alert("Messages cannot contain email addresses or phone numbers.");
      return;
    }

    let fileUrl = null;
    let fileType = null;

    try {
      setUploading(true);

      if (pendingFile) {
        const ext = pendingFile.name.split(".").pop();
        const storageRef = ref(
          storage,
          `sharedChats/${hashcode}/files/${Date.now()}.${ext}`
        );
        await uploadBytes(storageRef, pendingFile);
        fileUrl = await getDownloadURL(storageRef);
        fileType = pendingFile.type.startsWith("image/") ? "image" : "file";
      }

      await addDoc(collection(db, `sharedChats/${hashcode}/messages`), {
        text: trimmedText,
        fileUrl,
        fileType,
        timestamp: serverTimestamp(),
        from: userType,
      });

      setInput("");
      setPendingFile(null);
      setFilePreviewUrl(null);
    } catch (err) {
      console.error("❌ Failed to send message:", err);
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPendingFile(file);
      setFilePreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleEmojiSelect = (emoji) => {
    setInput((prev) => prev + emoji.native);
  };

  const handleTyping = (e) => {
    setInput(e.target.value);
    setIsTyping(true);
    clearTimeout(typingTimeout.current);
    typingTimeout.current = setTimeout(() => {
      setIsTyping(false);
    }, 1500);
  };

  return (
    <div className="chatbox-wrapper">
      <div className="chatbox-header">💬 Live Chat</div>

      <div className="chatbox-body">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`chat-bubble ${msg.from === userType ? "sent" : "received"}`}
          >
            {msg.fileUrl && msg.fileType === "image" && (
              <img src={msg.fileUrl} alt="Attachment" className="chat-image" />
            )}
            {msg.fileUrl && msg.fileType === "file" && (
              <a
                href={msg.fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="chat-file"
              >
                📎 View File
              </a>
            )}
            {msg.text && <span className="chat-text">{msg.text}</span>}
            {msg.timestamp && (
              <div className="chat-timestamp">
                {new Date(msg.timestamp.seconds * 1000).toLocaleString()}
              </div>
            )}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {filePreviewUrl && (
        <div className="chatbox-preview">
          <img src={filePreviewUrl} alt="Preview" className="chatbox-preview-img" />
          <button
            onClick={() => {
              setPendingFile(null);
              setFilePreviewUrl(null);
            }}
            className="chatbox-remove-preview"
          >
            ✖
          </button>
        </div>
      )}

      {isTyping && <div className="typing-indicator">Typing...</div>}

      <div className="chatbox-footer">
  <label className="chat-upload">
    📎
    <input
      type="file"
      accept="image/*,.pdf"
      onChange={handleFileChange}
      hidden
    />
  </label>

  <input
    type="text"
    placeholder={pendingFile ? "Add a caption or note..." : "Type a message..."}
    value={input}
    onChange={handleTyping}
    onKeyDown={(e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    }}
    disabled={uploading}
  />

  <button
    onClick={sendMessage}
    disabled={uploading || (!input.trim() && !pendingFile)}
  >
    {uploading ? <span className="spinner"></span> : "➤"}
  </button>
</div>


      
    </div>
  );
};

export default ChatBox;
